import { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import { usePremium } from "../context/PremiumContext";
import { tourActive } from "../lib/tour";
import { APP_NAME, APP_PLAY_URL } from "../lib/appLinks";

const DAY = 86400000;

// Occasionally (and only at a genuinely happy moment) invites a Google Play
// rating — never during the tour, once at first, once much later at most.
export default function ReviewPrompt() {
  const { inApp } = usePremium();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onHappy = () => {
      if (!inApp || tourActive()) return;
      try {
        if (localStorage.getItem("cb_reviewed") === "1") return;
        const happy = (parseInt(localStorage.getItem("cb_happy_count") || "0", 10) || 0) + 1;
        localStorage.setItem("cb_happy_count", String(happy));
        const asked = parseInt(localStorage.getItem("cb_review_asked") || "0", 10) || 0;
        const last = parseInt(localStorage.getItem("cb_review_last") || "0", 10) || 0;
        const due = (happy >= 3 && asked === 0) || (happy >= 12 && asked === 1 && Date.now() - last > 30 * DAY);
        if (due) {
          localStorage.setItem("cb_review_asked", String(asked + 1));
          localStorage.setItem("cb_review_last", String(Date.now()));
          setTimeout(() => setShow(true), 1200);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("cb-happy-moment", onHappy);
    return () => window.removeEventListener("cb-happy-moment", onHappy);
  }, [inApp]);

  if (!show) return null;
  const rate = () => {
    try { localStorage.setItem("cb_reviewed", "1"); } catch { /* ignore */ }
    window.open(APP_PLAY_URL, "_blank");
    setShow(false);
  };
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-5 backdrop-blur-sm" data-testid="review-prompt">
      <div className="relative w-full max-w-sm rounded-3xl border border-border bg-background p-7 text-center shadow-2xl">
        <button onClick={() => setShow(false)} data-testid="review-close" className="absolute right-4 top-4 text-muted-foreground transition-colors hover:text-foreground">
          <X className="h-5 w-5" />
        </button>
        <div className="mb-3 flex justify-center gap-1 text-primary">
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-6 w-6 fill-current" />)}
        </div>
        <h2 className="font-serif-display text-2xl tracking-tight">Enjoying {APP_NAME}?</h2>
        <p className="font-ui mt-2 text-sm text-muted-foreground">
          A quick rating on Google Play helps others discover a calmer way to read the Word. It only takes a moment.
        </p>
        <button
          onClick={rate}
          data-testid="review-rate"
          className="font-ui mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"
        >
          <Star className="h-4 w-4" /> Rate on Google Play
        </button>
        <button onClick={() => setShow(false)} data-testid="review-later" className="font-ui mt-3 w-full text-sm text-muted-foreground hover:text-foreground">
          Maybe later
        </button>
      </div>
    </div>
  );
}
